import Modal from "components/Modal"
import Input from "components/utils/InputField"
import Selector from "components/utils/Selector"
import Toggle from "components/utils/Toggle"
import { DocumentReference, getDoc, setDoc, updateDoc } from "firebase/firestore"
import useBasicInfos from "hooks/firestore/advanced/useBasicInfos"
import { useDocument } from "hooks/firestore/core/useDocument"
import { getDiscountCodeRef } from "hooks/firestore/getRefs"
import { ChangeEvent, FormEvent, useEffect, useState } from "react"
import DiscountCode, { createDefaultDiscountCode } from "types/firestore/discountCode"
// eslint-disable-next-line css-modules/no-unused-class
import styles from "./DiscountCodes.module.scss"

interface DiscountCodeEditModalProps {
  docRef: DocumentReference<DiscountCode> | "newCode" | undefined
  close: () => void
}

export default function DiscountCodeEditModal({ docRef, close }: DiscountCodeEditModalProps) {
  const editing = docRef && docRef !== "newCode"
  const { document } = useDocument<DiscountCode>(
    docRef === "newCode" ? undefined : docRef && (docRef as DocumentReference<DiscountCode>)
  )

  const [localValues, setLocalValues] = useState<DiscountCode>(createDefaultDiscountCode())
  const [initiallyLoaded, setInitiallyLoaded] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  useEffect(() => {
    if (editing) {
      if (!document || initiallyLoaded) {
        setLocalValues(createDefaultDiscountCode())
        return
      }
      // alert(JSON.stringify(document))
      setInitiallyLoaded(true)
      setLocalValues({ ...document, code: document._ref.id })
      return
    }
    setLocalValues(createDefaultDiscountCode())
    return
  }, [document])

  const closeModal = () => {
    setInitiallyLoaded(false)
    setLocalValues(createDefaultDiscountCode())
    setErrors([])
    close()
  }

  const editLocalValue = (e: ChangeEvent<HTMLInputElement>) => {
    const property = e.target.name
    let value: string | number = e.target.value
    if (property === "code") {
      value = value.toUpperCase()
      if (!(/^[A-Z0-9]*$/.test(value) && value.length <= 12)) {
        return
      }
    }
    const type = e.target.type
    if (type === "number") {
      value = Math.max(0, parseInt(value))
    }
    setErrors(errors.filter(e => e !== property))
    setLocalValues({ ...localValues, [property]: value })
  }

  const selectType = (type: "euro" | "percent") => {
    if (type === localValues.type) {
      return
    }
    return setLocalValues({ ...localValues, type, value: 10 })
  }

  const { shop } = useBasicInfos()

  const saveChange = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!docRef) return

    const err = [...errors]
    const { value, limitUses } = localValues
    if (value < 0) err.push("value")
    if (limitUses < 0) err.push("limitUses")

    if (docRef === "newCode") {
      // New Code Ready to set up
      const shopId = shop?._ref.id
      if (!shopId) {
        setErrors(err)
        return
      }
      const code = localValues.code
      if (!code) {
        err.push("code")
        setErrors(err)
        return
      }
      // Check if code is existing already, if yes, break and give error
      const document = await getDoc(getDiscountCodeRef(shopId, code))
      if (document.exists()) {
        err.push("code")
        setErrors(err)
        return
      }
      if (!err.length) {
        const ref = document.ref
        await setDoc(ref, localValues)
        return closeModal()
      }
    }
    if (docRef && docRef !== "newCode") {
      if (!err.length) {
        const { countUsed, code, ...updates } = localValues
        updateDoc(docRef, updates)
        return closeModal()
      }
    }
    setErrors(err)
    return
  }

  return (
    <>
      <Modal
        {...{
          open: !!docRef,
          onClose: closeModal,
          title: editing ? "Discount Code anpassen" : "Neuen Code anlegen",
        }}
      >
        <div className={styles.modalBody}>
          <form onSubmit={saveChange} className={styles.form}>
            {errors.includes("code") && (
              <p style={{ color: "var(--danger)", margin: 0 }}>
                {localValues.code
                  ? "Dieser Code wird bereits verwendet. Bitte wähle einen anderen Code"
                  : "Bitte setze einen Code"}
              </p>
            )}
            <div className={styles.flexRow}>
              <Input
                danger={errors.includes("code")}
                value={localValues.code}
                name="code"
                onChange={editLocalValue}
                disabled={editing}
                className={styles.flexAuto}
              >
                Code
              </Input>
              <Input
                type="number"
                value={localValues.limitUses.toString()}
                name="limitUses"
                onChange={editLocalValue}
                danger={errors.includes("limitUses")}
                containerChildren={<span className={styles.footnote}>(0 für unbegrenzt)</span>}
                className={styles.flexAuto}
              >
                Anzahl Nutzungen
              </Input>
              <Toggle {...{ state: true, setState: v => setLocalValues({ ...localValues, active: v }) }}>Aktiv</Toggle>
            </div>
            <div className={styles.flexRow}>
              <Selector
                items={[
                  { value: "euro", label: "€" },
                  { value: "percent", label: "%" },
                ]}
                value={[localValues.type]}
                onChange={values => selectType(values[0])}
              />
              {localValues.type === "euro" ? (
                <Input
                  type="currency"
                  value={(localValues.value / 100).toString()}
                  name="value"
                  onChange={e => {
                    e.target.value = Math.floor(parseFloat(e.target.value) * 100).toString()
                    return editLocalValue(e)
                  }}
                  danger={errors.includes("value")}
                  className={styles.flexAuto}
                >
                  Wert
                </Input>
              ) : (
                <Input
                  type="number"
                  value={localValues.value.toString()}
                  name="value"
                  onChange={editLocalValue}
                  danger={errors.includes("value")}
                  className={styles.flexAuto}
                >
                  Wert
                </Input>
              )}
            </div>

            <button className={styles.submitButton} type="submit">
              Speichern
            </button>
          </form>
        </div>
      </Modal>
    </>
  )
}
